import {
  type EditorModel,
  type EditorModelWithoutRelations,
} from '@/lib/types';
import { useContext, useState } from 'react';
import ModelsContext from '../../ModelsContext';
import Dialog from '@/components/Dialog';
import useApi from '@/lib/api/useApi';
import { Button, TextField } from '@mui/material';
import styled from '@emotion/styled';
import { FormattedMessage } from 'react-intl';
import useComplexSwrState from '@/lib/utils/useComplexSwrState';

const EditorWrapper = styled('div')({
  marginTop: 16,
  display: 'flex',
  '.MuiTextField-root': {
    width: 256,
    marginRight: 12,
  },
});

type ModelNameEditorProps = {
  model: EditorModel;
};

const ModelNameEditor = ({ model }: ModelNameEditorProps) => {
  const api = useApi();

  const [, setModels] = useContext(ModelsContext);

  const [name, setName, , reenableNameSync] = useComplexSwrState(
    model.name ?? '',
  );
  const [saving, setSaving] = useState(false);

  const saveNameChange = async (): Promise<void> => {
    setSaving(true);

    const updatedModel = await api.put<EditorModelWithoutRelations>(
      `editor/challenge-model/models/${model.id}/name`,
      {
        name,
      },
    );

    setModels(models => {
      models[model.id] = {
        ...models[model.id],
        ...updatedModel,
      };
    });

    setSaving(false);

    reenableNameSync();
  };

  return (
    <div>
      <h3>
        <FormattedMessage id="editor.model_editor.model_selector.model_settings.dialog.name_editor.title" />
      </h3>
      <Dialog.ContentText>
        <FormattedMessage id="editor.model_editor.model_selector.model_settings.dialog.name_editor.text" />
      </Dialog.ContentText>
      <EditorWrapper>
        <TextField
          size="small"
          label={
            <FormattedMessage id="editor.model_editor.model_selector.model_settings.dialog.name_editor.input_label" />
          }
          value={name}
          onChange={e => setName(e.target.value)}
        />
        <Button
          variant="contained"
          onClick={saveNameChange}
          disabled={
            name.trim() === model.name?.trim() || name.trim().length === 0
          }
          loading={saving}
          disableElevation
        >
          <FormattedMessage id="dialogs.actions.save" />
        </Button>
      </EditorWrapper>
    </div>
  );
};

export default ModelNameEditor;
